import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useCrashTrackChartDataQuery } from "../../api/network/adminApiServices";
import { DASHBOARD_WIDGET_TYPE } from "../../api/types/Overview";
import svgIcons from "../../assets/svg/svgIcons";
import WidgetChartAreaGenerator from "../overview/components/widgetChartAreaGenerator";
import { EWidgetChartTypes } from "../overview/types/overview.types";
import { crashtrack_tab_slugs } from "./crash-track-admin-details";
import { CrashTrackTabParams } from "./type";
import { APP_CONFIG } from "../../constants/constants";

const ScreenCrashTrackTabAccelerationGraph = ({
  assetCrashIdAsset,
  assetCrashIdTimestamp,
  onLoadComplete,
  printMode = false,
}: CrashTrackTabParams) => {
  const tab_slug = crashtrack_tab_slugs[3]; // "acceleration_graph";
  const { t: tCrashTrackDetails } = useTranslation("translation", {
    keyPrefix: `admins.crashtrack.detailsPage`,
  });
  const { t } = useTranslation("translation", {
    keyPrefix: `admins.crashtrack.detailsPage.tab_${tab_slug}`,
  });
  const hasCalledLoadComplete = useRef(false);

  const {
    data: dataCrashTrackChartData,
    isFetching: isFetchingCrashTrackChartData,
    isError: isErrorCrashTrackChartData,
  } = useCrashTrackChartDataQuery(
    { assetId: assetCrashIdAsset, timestamp: assetCrashIdTimestamp },
    { skip: !assetCrashIdAsset || !assetCrashIdTimestamp }
  );
  useEffect(() => {
    if (
      printMode &&
      !isFetchingCrashTrackChartData &&
      !hasCalledLoadComplete.current
    ) {
      onLoadComplete?.();
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          "===== CRASH TRACK TAB LOADED",
          tab_slug,
          new Date().getTime()
        );
      hasCalledLoadComplete.current = true;
    }
  }, [printMode, isFetchingCrashTrackChartData, onLoadComplete]);

  if (isFetchingCrashTrackChartData)
    return (
      <div className="flex-grow flex items-center justify-center min-h-64">
        <img src={svgIcons.LoadingAnimation} alt="" className="size-12" />
      </div>
    );

  if (
    isErrorCrashTrackChartData ||
    !dataCrashTrackChartData?.impact_details ||
    dataCrashTrackChartData?.impact_details?.length === 0
  ) {
    return (
      <div className="flex flex-col items-center justify-center min-h-64 text-center">
        <p className="font-bold text-xl text-gray-400 px-8">
          {tCrashTrackDetails("tab_data_fetch_error")}
        </p>
      </div>
    );
  }

  return (
    <div className="printable-landscape-page flex flex-col gap-4 p-4">
      <h2 className="font-semibold text-lg">{t("heading")}</h2>
      <div className="flex flex-col bg-white rounded-lg">
        <WidgetChartAreaGenerator
          barChartMargin={{ top: 20, right: 20, left: -35, bottom: 20 }}
          // cellItemsForSectionColor={dataCrashTrackChartData?.impact_details}
          chartGroupingLabels={[
            "acceleration_x",
            "acceleration_y",
            "acceleration_z",
          ]}
          // chartHighlightLabel={`Total ${selectedGroup?.name}`}
          // chartHighlightValue={dataReport?.count?.toString() ?? "0"}
          chartType={EWidgetChartTypes.LINE_GRAPH}
          chartWrapperHeightClass={"h-[525px]"}
          computedChartData={dataCrashTrackChartData?.impact_details?.map(
            (item) => ({
              acceleration_x: item.acceleration_x,
              acceleration_y: item.acceleration_y,
              acceleration_z: item.acceleration_z,
              elapsed_time: item.elapsed_time,
            })
          )}
          graphXAxisDataKey={"elapsed_time"}
          graphYAxisDataKey={"acceleration_x"}
          legendFormatter={(value, entry, index) => (
            <span
              key={`item-${index}`}
              style={{
                color: entry.color,
                fontSize: "10px",
              }}
            >
              {value}
            </span>
          )}
          showChartTypeSwitchButton={false}
          skipYaxisDayaKeyInLineChart={true}
          t={(value: any) => value}
          // tooltipEnabled={true}
          tooltipFormatter={(value: any, name: any) => {
            return [`${name}: ${!!value ? parseFloat(value).toFixed(2) : 0}`];
          }}
          widgetType={DASHBOARD_WIDGET_TYPE.CUSTOM}
          xAxisTickLabelWidth={80}
          xAxisTickFormatter={(value: number) => {
            // expect value to be in seconds
            // display in HH:MM:SS format
            const sec_num = parseInt(value?.toString(), 10); // don't forget the second param
            const hours = Math.floor(sec_num / 3600);
            const minutes = Math.floor((sec_num - hours * 3600) / 60);
            const seconds = sec_num - hours * 3600 - minutes * 60;

            return [hours, minutes, seconds]
              .map((v) => (v < 10 ? "0" + v : v))
              .filter((v, i) => v !== "00" || i > 0) // remove leading hours if 0
              .join(":");
          }}
        />
      </div>
    </div>
  );
};
export default ScreenCrashTrackTabAccelerationGraph;
